/**
 * @fileoverview Componente de gerenciamento de categorias (admin).
 * Permite edição de status (enable/disable) e upload de imagens.
 * NÃO permite criação ou exclusão de categorias.
 * 
 * @module CategoryManager
 * @since 1.0.0
 */

import { useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Paper,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  Tooltip,
  Chip,
  TextField,
  Alert,
  Snackbar,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CategoryEditModal from './CategoryEditModal';
import { apiRequestJson } from '../../api/apiService';
import { updateCategory } from '../../store/slices/productsSlice';
import { setCategories } from '../../store/slices/globalDataSlice';
import { useGlobalSearch } from '../../hooks/useGlobalSearch';
import { useLocalData } from '../../hooks/useLocalData';
import globalContextManager from '../../utils/globalContextManager';

/**
 * Componente de gerenciamento de categorias para administradores.
 * @component
 * @param {Object} props
 * @param {boolean} props.open - Estado de abertura do modal
 * @param {Function} props.onClose - Callback para fechar modal
 * @returns {JSX.Element}
 */
export default function CategoryManager({ open, onClose }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { searchAllCategories } = useGlobalSearch();
  const { categories: localCategories } = useLocalData();

  const [categoriesLocal, setCategoriesLocal] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('name');
  const [order, setOrder] = useState('asc');

  /**
   * Busca categorias da API e atualiza estado global.
   * Chamado como fallback quando localCategories está vazio.
   * @async
   */
  const fetchCategoriesFromApi = async () => {
    try {
      const response = await apiRequestJson('products_categories');
      if (response.success && response.data) {
        dispatch(setCategories(response.data));
        setCategoriesLocal(response.data);
        console.log('✅ Categories carregadas da API:', response.data.length);
      }
    } catch (error) {
      console.error('Erro ao buscar categories da API:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setLoading(true);
      if (localCategories.length > 0) {
        setCategoriesLocal(localCategories);
        setLoading(false);
      } else {
        fetchCategoriesFromApi();
      }
    }
  }, [open, localCategories]);

  /**
   * Exibe mensagem de feedback.
   */
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  /**
   * Manipula ordenação.
   */
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /**
   * Categorias filtradas e ordenadas.
   * Usa pesquisa global que considera TODAS as categorias disponíveis.
   */
  const filteredAndSortedCategories = useMemo(() => {
    // Usa pesquisa global para garantir busca em TODAS as categorias
    let filtered = searchTerm ? searchAllCategories(searchTerm) : categoriesLocal;

    // Ordenação
    return [...filtered].sort((a, b) => {
      let aValue = a[orderBy] || '';
      let bValue = b[orderBy] || '';

      if (orderBy === 'name') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      }
      return aValue < bValue ? 1 : -1;
    });
  }, [categoriesLocal, searchTerm, order, orderBy, searchAllCategories]);

  /**
   * Abre modal de edição.
   */
  const handleEdit = (category) => {
    setSelectedCategory(category);
    setEditModalOpen(true);
  };

  /**
   * Salva alterações da categoria.
   * Após sucesso, atualiza contexto global e recarrega dados locais.
   */
  const handleSaveCategory = async (data) => {
    try {
      await dispatch(updateCategory(data)).unwrap();
      
      // Atualiza contexto global após operação bem-sucedida
      await globalContextManager.forceUpdate('categories');
      
      showSnackbar('Categoria atualizada com sucesso');
    } catch (error) {
      showSnackbar('Erro ao atualizar categoria', 'error');
      throw error;
    }
  };

  /**
   * Executa busca.
   */
  const handleSearch = () => {
    setSearchTerm(searchInput);
  };

  /**
   * Limpa busca.
   */
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchTerm('');
  };

  /**
   * Renderiza grid desktop.
   */
  const renderDesktopGrid = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'id'}
                direction={orderBy === 'id' ? order : 'asc'}
                onClick={() => handleSort('id')}
              >
                ID
              </TableSortLabel>
            </TableCell>
            <TableCell>Imagem</TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'name'}
                direction={orderBy === 'name' ? order : 'asc'}
                onClick={() => handleSort('name')}
              >
                Nome
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'enable'}
                direction={orderBy === 'enable' ? order : 'asc'}
                onClick={() => handleSort('enable')}
              >
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredAndSortedCategories
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((category) => (
              <TableRow key={category.id}>
                <TableCell>{category.id}</TableCell>
                <TableCell>
                  {category.image_url ? (
                    <Box
                      component="img"
                      src={category.image_url}
                      alt={category.name}
                      sx={{
                        width: 40,
                        height: 40,
                        objectFit: 'cover',
                        borderRadius: 1
                      }}
                    />
                  ) : (
                    <Box sx={{ width: 40, height: 40, bgcolor: 'grey.200', borderRadius: 1 }} />
                  )}
                </TableCell>
                <TableCell>{category.name}</TableCell>
                <TableCell>
                  <Chip
                    label={category.enable ? 'Ativa' : 'Inativa'}
                    color={category.enable ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => handleEdit(category)}>
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  /**
   * Renderiza grid mobile.
   */
  const renderMobileGrid = () => (
    <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Imagem</TableCell>
              <TableCell>Nome</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedCategories
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((category) => (
                <TableRow key={category.id}>
                  <TableCell>
                    {category.image_url ? (
                      <Box
                        component="img"
                        src={category.image_url}
                        alt={category.name}
                        sx={{
                          width: 32,
                          height: 32,
                          objectFit: 'cover',
                          borderRadius: 1
                        }}
                      />
                    ) : (
                      <Box sx={{ width: 32, height: 32, bgcolor: 'grey.200', borderRadius: 1 }} />
                    )}
                  </TableCell>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>
                    <Chip
                      label={category.enable ? 'Ativa' : 'Inativa'}
                      color={category.enable ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleEdit(category)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          Gerenciamento de Categorias
          <IconButton
            onClick={onClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Buscar por nome..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <IconButton size="small" onClick={handleSearch} title="Buscar">
                    <SearchIcon />
                  </IconButton>
                ),
                endAdornment: searchInput && (
                  <IconButton size="small" onClick={handleClearSearch} title="Limpar">
                    <CloseIcon />
                  </IconButton>
                )
              }}
              sx={{ flexGrow: 1, minWidth: 200 }}
            />
          </Box>

          {isMobile ? renderMobileGrid() : renderDesktopGrid()}

          <TablePagination
            component="div"
            count={filteredAndSortedCategories.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <CategoryEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        category={selectedCategory}
        onSave={handleSaveCategory}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
